import React, { useState, useCallback, useEffect } from 'react';
import { api } from '../api/api';
import ReactFlow, { 
    Background, Controls, addEdge, applyNodeChanges, applyEdgeChanges, Handle, Position, MarkerType 
} from 'reactflow';
import { useSelector } from 'react-redux';
import 'reactflow/dist/style.css';
import { Zap, Mail, Clock, HelpCircle, XCircle, Save, Play, Square, Split, Briefcase, Tag, CheckCircle2, LayoutTemplate, UploadCloud, UserCheck, Wand2, Trash2, Plus, ArrowDown, ShieldCheck } from 'lucide-react';
import SequenceEmailEditorDrawer from './SequenceEmailEditorDrawer';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

// Custom Node Handle Styling
const handleClass = "w-3 h-3 !bg-background border-2 border-primary hover:!bg-primary transition-colors";

// Custom Nodes Design
const StartNode = ({ data, isConnectable }) => (
    <div className="bg-emerald-600 text-white rounded-full p-4 px-8 font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl border-2 border-emerald-400/20 flex items-center gap-2 relative ring-4 ring-emerald-500/10 animate-pulse">
        <Play className="w-4 h-4 fill-current" /> START
        <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} id="out" className={`${handleClass} !bg-emerald-500 !border-emerald-300`} />
    </div>
);

const StopNode = ({ data, isConnectable }) => (
    <div className="bg-rose-600 text-white rounded-full p-4 px-8 font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl border-2 border-rose-400/20 flex items-center gap-2 relative ring-4 ring-rose-500/10">
        <Handle type="target" position={Position.Top} isConnectable={isConnectable} id="in" className={`${handleClass} !bg-rose-500 !border-rose-300`} />
        <Square className="w-4 h-4 fill-current" /> STOP
    </div>
);

const EmailNode = ({ data, selected, isConnectable }) => (
    <div className={`bg-card w-[250px] rounded-xl shadow-md border-2 ${selected ? 'border-primary' : 'border-border'} relative`}>
        <Handle type="target" position={Position.Top} isConnectable={isConnectable} id="in" className={handleClass} />
        <div className="bg-blue-500/10 border-b p-2 px-3 flex items-center justify-between gap-2 rounded-t-[10px]">
            <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-500" />
                <span className="font-bold text-sm">Send Email</span>
            </div>
            <div className="flex items-center gap-1.5">
                {data.isDraft && (
                    <span className="text-[8px] font-black uppercase text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded border border-amber-200 shadow-sm animate-pulse">
                        Draft
                    </span>
                )}
                {data.stepLabel && (
                    <span className="text-[9px] font-black uppercase text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                        {data.stepLabel.replace(/_/g, ' ')}
                    </span>
                )}
            </div>
        </div>
        <div className="p-3 bg-background rounded-b-[10px]">
            <div className="text-xs font-semibold truncate">Sub: {data.subjectTemplate || 'No Subject'}</div>
            <div className="text-[10px] text-muted-foreground mt-1 line-clamp-2">
                {data.bodyTemplate ? data.bodyTemplate.replace(/<[^>]*>?/gm, '') : 'Empty Body'}
            </div>
        </div>
        <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} id="out" className={handleClass} />
    </div>
);


const WaitNode = ({ data, selected, isConnectable }) => (
    <div className={`bg-card w-[180px] rounded-xl shadow-md border-2 ${selected ? 'border-primary' : 'border-border'} relative`}>
        <Handle type="target" position={Position.Top} isConnectable={isConnectable} id="in" className={handleClass} />
        <div className="bg-orange-500/10 border-b p-2 px-3 flex items-center gap-2 rounded-t-[10px]">
            <Clock className="w-4 h-4 text-orange-500" />
            <span className="font-bold text-sm">Wait</span>
        </div>
        <div className="p-3 bg-background text-center flex flex-col items-center rounded-b-[10px]">
            <span className="text-sm font-semibold mb-2">{data.delayDays || 1} day(s)</span>
            
            {/* Live Analytics Bubble */}
            {data.stats?.pending > 0 && (
                <div className="bg-yellow-100 text-yellow-800 text-[10px] uppercase font-black px-2 py-0.5 rounded-full outline outline-1 outline-yellow-400 absolute -top-2 -right-2 shadow-sm animate-pulse flex items-center gap-1">
                    <Clock className="w-2 h-2" /> {data.stats.pending} Waiting
                </div>
            )}
        </div>
        <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} id="out" className={handleClass} />
    </div>
);

const TaskNode = ({ data, selected, isConnectable }) => {
    let title = "Manual Task";
    let icon = <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    
    if (data.type === 'TASK_LINKEDIN_MANUAL') {
        title = "LinkedIn Action";
        icon = <Briefcase className="w-4 h-4 text-blue-600" />;
    }
    
    return (
        <div className={`bg-card w-[220px] rounded-xl shadow-md border-2 ${selected ? 'border-primary' : 'border-border'} relative`}>
            <Handle type="target" position={Position.Top} isConnectable={isConnectable} id="in" className={handleClass} />
            <div className="bg-emerald-500/10 border-b p-2 px-3 flex items-center gap-2 rounded-t-[10px]">
                {icon}
                <span className="font-bold text-sm tracking-tight">{title}</span>
            </div>
            <div className="p-3 bg-background rounded-b-[10px]">
                <div className="text-xs font-semibold truncate text-foreground">{data.taskTitle || 'Pending Action'}</div>
                <div className="text-[10px] text-muted-foreground mt-1 line-clamp-2">Pauses sequence until resolved by workspace operator natively via Task Queue.</div>
            </div>
            <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} id="out" className={handleClass} />
        </div>
    );
};

const UpdateNode = ({ data, selected, isConnectable }) => {
    const isTag = data.type === 'TAG_UPDATE';
    return (
        <div className={`bg-card w-[180px] rounded-xl shadow-md border-2 ${selected ? 'border-primary' : 'border-border'} relative`}>
            <Handle type="target" position={Position.Top} isConnectable={isConnectable} id="in" className={handleClass} />
            <div className="bg-slate-500/10 border-b p-2 px-3 flex items-center gap-2 rounded-t-[10px]">
                {isTag ? <Tag className="w-4 h-4 text-slate-600" /> : <Zap className="w-4 h-4 text-slate-600" />}
                <span className="font-bold text-sm tracking-tight">{isTag ? 'Add Tag' : 'Set Status'}</span>
            </div>
            <div className="p-3 bg-background flex justify-center text-xs font-black uppercase text-slate-700 bg-slate-50/50 rounded-b-[10px]">
                {isTag ? (data.tagToAdd || 'NO TAG') : (data.newStatus || 'NO STATUS')}
            </div>
            <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} id="out" className={handleClass} />
        </div>
    );
};

const ConditionNode = ({ data, selected, isConnectable }) => {
    let title = "Condition";
    let icon = <Split className="w-4 h-4 text-purple-500" />;
    
    if (data.type === 'IF_REPLIED') title = "If Replied";
    if (data.type === 'IF_BOUNCED') title = "If Bounced";
    if (data.type === 'IF_NO_REPLY') title = "If No Reply";

    return (
        <div className={`bg-card w-[200px] rounded-xl shadow-md border-2 ${selected ? 'border-primary' : 'border-border'} relative`}>
            <Handle type="target" position={Position.Top} isConnectable={isConnectable} id="in" className={handleClass} />
            <div className="bg-purple-500/10 border-b p-2 px-3 flex items-center gap-2 rounded-t-[10px]">
                {icon}
                <span className="font-bold text-sm">{title}</span>
            </div>
            <div className="flex bg-background rounded-b-[10px] overflow-visible divide-x border-t text-[10px] font-black uppercase tracking-widest relative">
                <div className={`flex-1 py-2 text-center relative ${data.type === 'IF_NO_REPLY' ? 'text-blue-600 bg-blue-50/50' : 'text-green-600 bg-green-50/50'}`}>
                    {data.type === 'IF_NO_REPLY' ? 'No Reply' : 'True'}
                    <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} id="true" className={`!left-[25%] !w-3.5 !h-3.5 border-2 border-white shadow-sm ${data.type === 'IF_NO_REPLY' ? '!bg-blue-500' : '!bg-green-500'}`} />
                </div>
                <div className={`flex-1 py-2 text-center relative ${data.type === 'IF_NO_REPLY' ? 'text-indigo-600 bg-indigo-50/50' : 'text-red-600 bg-red-50/50'}`}>
                    {data.type === 'IF_NO_REPLY' ? 'Replied' : 'False'}
                    <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} id="false" className={`!left-[75%] !w-3.5 !h-3.5 border-2 border-white shadow-sm ${data.type === 'IF_NO_REPLY' ? '!bg-indigo-500' : '!bg-red-500'}`} />
                </div>
            </div>
        </div>
    );
};

const nodeTypes = {
    start: StartNode,
    stop: StopNode,
    email: EmailNode,
    wait: WaitNode,
    condition: ConditionNode,
    task: TaskNode,
    update: UpdateNode
};

export default function SequenceGraphEditor({ sequence, onSave, onAutoSave, onCancel }) {
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [selectedNode, setSelectedNode] = useState(null);
    const [nodeStats, setNodeStats] = useState({});
    const [edgeStats, setEdgeStats] = useState({});
    
    // Simulator states
    const [simulationTrace, setSimulationTrace] = useState(null);
    const [isSimulating, setIsSimulating] = useState(false);
    const [mockEvents, setMockEvents] = useState({ replied: false, bounced: false, completeTasks: true, skipTasks: false });
    const [showSimulator, setShowSimulator] = useState(false);

    // Reminder Builder states
    const [showReminderBuilder, setShowReminderBuilder] = useState(false);
    const [reminderSteps, setReminderSteps] = useState([
        { label: 'INITIAL_EMAIL', delayDays: 0 },
        { label: 'REMINDER_1', delayDays: 3 },
        { label: 'REMINDER_2', delayDays: 4 },
        { label: 'FINAL_FOLLOW_UP', delayDays: 5 }
    ]);

    const hasDraftNodes = nodes.some((n) => {
        if (!n?.data) return false;
        const subject = String(n.data.subjectTemplate || '');
        const body = String(n.data.bodyTemplate || '');
        return (
            n.data.isDraft ||
            subject.includes('[DRAFT]') ||
            body.includes('[DRAFT CONTENT]') ||
            body.toLowerCase().includes('please edit this template before publishing')
        );
    });

    const { activeWorkspace } = useSelector((state) => state.workspace);
    const workspaceId = activeWorkspace?._id;
    
    const [editingEmailNode, setEditingEmailNode] = useState(false);

    // Fetch Stats natively
    useEffect(() => {
        if (!sequence?._id || !sequence?.isGraph) return;
        api.get(`/api/sequences/${sequence._id}/nodes/stats`)
           .then(res => { 
               if (res.data?.nodeStats) setNodeStats(res.data.nodeStats); 
               if (res.data?.edgeStats) setEdgeStats(res.data.edgeStats);
           })
           .catch(err => console.error("Stats fetch error:", err));
    }, [sequence?._id, sequence?.isGraph]);

    // Initial setup from default or saved layout
    useEffect(() => {
        if (sequence?.isGraph && sequence?.graph?.nodes?.length > 0) {
            setNodes(sequence.graph.nodes);
            setEdges(sequence.graph.edges);
        } else {
            // Default boilerplate
            const initialNodes = [
                { id: '1', type: 'start', position: { x: 250, y: 50 }, data: { type: 'START' } },
                { id: '2', type: 'email', position: { x: 250, y: 150 }, data: { type: 'SEND_EMAIL', subjectTemplate: 'First Outreach', bodyTemplate: 'Hi {{firstName}}...', stepLabel: 'INITIAL_EMAIL' } },
                { id: '3', type: 'stop', position: { x: 250, y: 300 }, data: { type: 'STOP' } }
            ];
            const initialEdges = [
                { id: 'e1-2', source: '1', target: '2', markerEnd: { type: MarkerType.ArrowClosed } },
                { id: 'e2-3', source: '2', target: '3', markerEnd: { type: MarkerType.ArrowClosed } }
            ];
            setNodes(initialNodes);
            setEdges(initialEdges);
        }
    }, [sequence]);

    const onNodesChange = useCallback(
        (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
        []
    );
    const onEdgesChange = useCallback(
        (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
        []
    );
    const onConnect = useCallback(
        (params) => setEdges((eds) => addEdge({ ...params, markerEnd: { type: MarkerType.ArrowClosed } }, eds)),
        []
    );

    const onSelectionChange = useCallback((params) => {
        if (params.nodes.length === 1) {
            setSelectedNode(params.nodes[0]);
        } else {
            setSelectedNode(null);
        }
    }, []);

    const addNode = (nodeFamily, specificType) => {
        const newNodeId = `node_${Date.now()}`;
        const newNode = {
            id: newNodeId,
            type: nodeFamily,
            position: { x: 300, y: 300 }, // Center-ish
            data: { type: specificType }
        };

        if (nodeFamily === 'email') {
            newNode.data.subjectTemplate = 'New Step Subject';
            newNode.data.bodyTemplate = 'New Step Body';
            newNode.data.stepLabel = 'INITIAL_EMAIL';
        }
        if (nodeFamily === 'wait') {
            newNode.data.delayDays = 1;
        }
        if (nodeFamily === 'task') {
            newNode.data.taskTitle = 'Review Contact Profile';
        }
        if (nodeFamily === 'update' && specificType === 'STATUS_UPDATE') {
            newNode.data.newStatus = 'INTERESTED';
        }

        setNodes([...nodes, newNode]);
    };

    const handleNodeDataChange = (field, value) => {
        if (!selectedNode) return;
        setNodes(nds => nds.map(n => {
            if (n.id === selectedNode.id) {
                const updatedNode = { ...n, data: { ...n.data, [field]: value } };
                setSelectedNode(updatedNode); // keep sidebar in sync
                return updatedNode;
            }
            return n;
        }));
    };

    const handleDeleteNode = () => {
        if (!selectedNode) return;
        setNodes(nds => nds.filter(n => n.id !== selectedNode.id));
        setEdges(eds => eds.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id));
        setSelectedNode(null);
    };

    const handleRunSimulation = async () => {
        setIsSimulating(true);
        setSimulationTrace(null);
        try {
            const res = await api.post(`/api/sequences/${sequence._id}/simulate`, {
                graphPayload: { nodes, edges },
                mockEvents
            });
            setSimulationTrace(res.data.trace);
        } catch (err) {
            console.error("Simulation failed", err);
            setSimulationTrace([{ actionType: 'ERROR', details: { reason: err?.response?.data?.error || 'Validation crash securely.' } }]);
        } finally {
            setIsSimulating(false);
        }
    };

    const STEP_LABEL_OPTIONS = [
        { value: 'INITIAL_EMAIL', label: 'Initial Email' },
        { value: 'REMINDER_1', label: 'Reminder 1' },
        { value: 'REMINDER_2', label: 'Reminder 2' },
        { value: 'REMINDER_3', label: 'Reminder 3' },
        { value: 'FINAL_FOLLOW_UP', label: 'Final Follow-up' },
        { value: 'OTHER', label: 'Other' }
    ];

    const updateReminderStep = (index, field, value) => {
        setReminderSteps(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
    };

    const addReminderStep = () => {
        if (reminderSteps.length >= 6) {
            toast.warning("Maximum 6 stages recommended for sequence stability.");
            return;
        }
        const nextIndex = reminderSteps.length;
        const nextLabel = nextIndex <= 3 ? `REMINDER_${nextIndex}` : 'FINAL_FOLLOW_UP';
        setReminderSteps(prev => [...prev, { label: nextLabel, delayDays: 3 }]);
    };

    const removeReminderStep = (index) => {
        if (reminderSteps.length <= 2) return;
        setReminderSteps(prev => prev.filter((_, i) => i !== index));
    };

    const generateReminderGraph = () => {
        const generatedNodes = [];
        const generatedEdges = [];
        let y = 50;
        const x = 300;
        const spacing = 120;
        let prevNodeId = null;
        let prevHandleId = 'out';

        // START node
        const startId = `rb_start_${Date.now()}`;
        generatedNodes.push({ id: startId, type: 'start', position: { x, y }, data: { type: 'START' } });
        prevNodeId = startId;
        y += spacing;

        // Reply-stop branch collector (shared stop node for all "replied" outcomes)
        const stopReplyId = `rb_stop_reply_${Date.now()}`;
        generatedNodes.push({ id: stopReplyId, type: 'stop', position: { x: x + 350, y: 50 }, data: { type: 'STOP' } });

        for (let i = 0; i < reminderSteps.length; i++) {
            const step = reminderSteps[i];
            const ts = Date.now() + i * 10;
            const labelText = STEP_LABEL_OPTIONS.find(o => o.value === step.label)?.label || step.label;

            // SEND_EMAIL node
            const emailId = `rb_email_${ts}`;
            generatedNodes.push({
                id: emailId,
                type: 'email',
                position: { x, y },
                data: {
                    type: 'SEND_EMAIL',
                    isDraft: true,
                    stepLabel: step.label,
                    subjectTemplate: `[DRAFT] ${labelText} — {{companyName}}`,
                    bodyTemplate: `Hi {{firstName}},\n\n[DRAFT CONTENT]\nThis is your ${labelText.toLowerCase()}. Please edit this template before publishing.`
                }
            });

            // Edge from previous node
            generatedEdges.push({
                id: `rb_e_${prevNodeId}_${emailId}`,
                source: prevNodeId,
                sourceHandle: prevHandleId,
                target: emailId,
                targetHandle: 'in',
                markerEnd: { type: MarkerType.ArrowClosed }
            });
            y += spacing;

            // If not last step, add WAIT + IF_NO_REPLY
            if (i < reminderSteps.length - 1) {
                const nextStep = reminderSteps[i + 1];

                // WAIT node
                const waitId = `rb_wait_${ts}`;
                generatedNodes.push({
                    id: waitId,
                    type: 'wait',
                    position: { x, y },
                    data: { type: 'WAIT', delayDays: nextStep.delayDays }
                });
                generatedEdges.push({
                    id: `rb_e_${emailId}_${waitId}`,
                    source: emailId, sourceHandle: 'out',
                    target: waitId, targetHandle: 'in',
                    markerEnd: { type: MarkerType.ArrowClosed }
                });
                y += spacing;

                // IF_NO_REPLY condition
                const condId = `rb_cond_${ts}`;
                generatedNodes.push({
                    id: condId,
                    type: 'condition',
                    position: { x, y },
                    data: { type: 'IF_NO_REPLY' }
                });
                generatedEdges.push({
                    id: `rb_e_${waitId}_${condId}`,
                    source: waitId, sourceHandle: 'out',
                    target: condId, targetHandle: 'in',
                    markerEnd: { type: MarkerType.ArrowClosed }
                });

                // False (replied) → shared STOP
                generatedEdges.push({
                    id: `rb_e_${condId}_stop_${ts}`,
                    source: condId, sourceHandle: 'false',
                    target: stopReplyId, targetHandle: 'in',
                    markerEnd: { type: MarkerType.ArrowClosed }
                });

                prevNodeId = condId;
                prevHandleId = 'true'; // no-reply path continues
                y += spacing;
            } else {
                // Last email → STOP (sequence complete)
                const stopEndId = `rb_stop_end_${ts}`;
                generatedNodes.push({ id: stopEndId, type: 'stop', position: { x, y }, data: { type: 'STOP' } });
                generatedEdges.push({
                    id: `rb_e_${emailId}_${stopEndId}`,
                    source: emailId, sourceHandle: 'out',
                    target: stopEndId, targetHandle: 'in',
                    markerEnd: { type: MarkerType.ArrowClosed }
                });
            }
        }

        // Reposition reply-stop node to be vertically centered
        const replyStop = generatedNodes.find(n => n.id === stopReplyId);
        if (replyStop) replyStop.position.y = y / 2;

        setNodes(generatedNodes);
        setEdges(generatedEdges);
        setShowReminderBuilder(false);
        setSelectedNode(null);
    };

    const handlePublish = () => {
        if (hasDraftNodes) {
            toast.error('Draft nodes must be finalized before publishing this sequence.');
            return;
        }
        onSave(nodes, edges);
    };

    return (
        <>
        {/* Reminder Builder Modal */}
        {showReminderBuilder && (
            <div className="fixed inset-0 z-[60] bg-background/80 backdrop-blur-sm flex justify-center items-center animate-in fade-in duration-200">
                <div className="bg-card text-card-foreground border-2 border-border/50 rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden">
                    <div className="p-8 border-b border-border/40 bg-gradient-to-r from-violet-500/5 to-blue-500/5">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-blue-500 text-white flex items-center justify-center shadow-lg">
                                <Wand2 className="w-5 h-5" />
                            </div>
                            <div>
                                <h2 className="text-lg font-black tracking-tight">Quick Build Reminder Flow</h2>
                                <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest">Guided Sequence Generator</p>
                            </div>
                        </div>
                    </div>

                    <div className="p-8 space-y-4 max-h-[50vh] overflow-y-auto custom-scrollbar">
                        {reminderSteps.map((step, idx) => (
                            <div key={idx} className="space-y-2">
                                {idx > 0 && (
                                    <div className="flex items-center gap-2 py-1 pl-4">
                                        <ArrowDown className="w-3.5 h-3.5 text-orange-500" />
                                        <span className="text-[10px] font-black uppercase tracking-widest text-orange-500">
                                            Wait {step.delayDays} day{step.delayDays !== 1 ? 's' : ''} • If no reply
                                        </span>
                                    </div>
                                )}
                                <div className="flex items-center gap-3 p-3 bg-muted rounded-xl border border-border/40">
                                    <div className="flex items-center gap-2 flex-1">
                                        <Mail className="w-4 h-4 text-blue-500 shrink-0" />
                                        <select
                                            className="text-sm font-bold bg-transparent border-none focus:outline-none flex-1"
                                            value={step.label}
                                            onChange={(e) => updateReminderStep(idx, 'label', e.target.value)}
                                        >
                                            {STEP_LABEL_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {idx > 0 && (
                                        <div className="flex items-center gap-1.5">
                                            <Clock className="w-3.5 h-3.5 text-orange-500" />
                                            <input
                                                type="number"
                                                min={1}
                                                className="w-14 text-sm font-bold border border-border/60 bg-background rounded-lg px-2 py-1 text-center"
                                                value={step.delayDays}
                                                onChange={(e) => updateReminderStep(idx, 'delayDays', parseInt(e.target.value) || 1)}
                                            />
                                            <span className="text-[10px] font-bold text-muted-foreground">days</span>
                                        </div>
                                    )}
                                    {idx > 0 && reminderSteps.length > 2 && (
                                        <button onClick={() => removeReminderStep(idx)} className="p-1.5 hover:bg-red-500/10 rounded-lg transition-colors">
                                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}

                        <button
                            onClick={addReminderStep}
                            className="w-full p-3 border-2 border-dashed border-border/60 rounded-xl text-sm font-bold text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors flex items-center justify-center gap-2"
                        >
                            <Plus className="w-4 h-4" /> Add Follow-up Stage
                        </button>
                    </div>

                    <div className="p-6 mx-8 mb-4 bg-slate-50 border border-slate-200 rounded-2xl">
                        <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
                            <ArrowDown className="w-3 h-3" /> Chain Preview
                        </h3>
                        <div className="flex flex-wrap gap-2">
                            {reminderSteps.map((s, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <div className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-[9px] font-black text-slate-700 shadow-sm uppercase">
                                        {STEP_LABEL_OPTIONS.find(o => o.value === s.label)?.label || s.label}
                                    </div>
                                    {i < reminderSteps.length - 1 && <span className="text-slate-400 text-[10px]">→</span>}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="p-4 mx-8 mb-4 bg-blue-500/5 border border-blue-500/10 rounded-xl">
                        <div className="flex items-start gap-2">
                            <ShieldCheck className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                            <div className="space-y-1">
                                <p className="text-[10px] font-bold text-blue-700 uppercase tracking-tight">Sequence Integrity Rules</p>
                                <p className="text-[10px] font-medium text-muted-foreground leading-relaxed">
                                    • <span className="font-bold text-foreground">Replies:</span> Any reply automatically pauses the sequence for that contact.<br/>
                                    • <span className="font-bold text-foreground">Bounces:</span> Delivery failures are handled by the runtime and stop all future reminders.<br/>
                                    • <span className="font-bold text-foreground">Drafts:</span> Generated nodes are marked as <span className="text-amber-600 font-bold underline">DRAFT</span>. You must edit them before publishing.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="p-8 pt-4 border-t border-border/40 flex gap-3">
                        <button
                            onClick={() => setShowReminderBuilder(false)}
                            className="flex-1 bg-muted border border-border/60 rounded-2xl px-6 h-11 font-black text-[10px] uppercase tracking-widest hover:bg-accent transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={generateReminderGraph}
                            className="flex-1 bg-gradient-to-r from-violet-500 to-blue-500 text-white rounded-2xl px-6 h-11 font-black text-[10px] uppercase tracking-[0.15em] shadow-lg hover:opacity-90 transition-all flex items-center justify-center gap-2"
                        >
                            <Wand2 className="w-4 h-4" /> Generate Graph
                        </button>
                    </div>
                </div>
            </div>
        )}


        <div className={cn(
            "flex h-[85vh] w-full border-2 rounded-[2.5rem] overflow-hidden shadow-[0_40px_100px_rgba(0,0,0,0.08)] bg-background transition-all duration-700",
            showSimulator ? "border-amber-500/40 ring-8 ring-amber-500/5 shadow-amber-500/10" : "border-border/60 hover:border-border"
        )}>
            {/* Canvas Area */}
            <div className="flex-1 relative group/canvas">
                {/* HUD Header Container */}
                <div className="absolute top-6 left-6 right-6 z-30 flex items-start justify-between gap-4 pointer-events-none">
                    {/* Left side: Node Toolbar */}
                    <div className={cn(
                        "flex flex-wrap max-w-2xl gap-2 bg-card/80 backdrop-blur-xl p-2 rounded-2xl shadow-xl border-2 border-border/40 transition-all duration-500 pointer-events-auto",
                        showSimulator ? "opacity-30 pointer-events-none scale-95 origin-top-left grayscale" : "opacity-100"
                    )}>
                        <button onClick={() => addNode('email', 'SEND_EMAIL')} className="p-2.5 bg-blue-500/5 border-2 border-transparent hover:border-blue-500/20 rounded-xl hover:bg-card font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95"><Mail className="w-3.5 h-3.5 text-blue-500"/> Email Step</button>
                        <button onClick={() => addNode('wait', 'WAIT')} className="p-2.5 bg-orange-500/5 border-2 border-transparent hover:border-orange-500/20 rounded-xl hover:bg-card font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95"><Clock className="w-3.5 h-3.5 text-orange-500"/> Delay Node</button>
                        <button onClick={() => addNode('condition', 'IF_REPLIED')} className="p-2.5 bg-purple-500/5 border-2 border-transparent hover:border-purple-500/20 rounded-xl hover:bg-card font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95"><Split className="w-3.5 h-3.5 text-purple-500"/> Logic Fork</button>
                        <div className="w-px h-6 bg-border/40 mx-1 self-center" />
                        <button onClick={() => addNode('task', 'TASK_LINKEDIN_MANUAL')} className="p-2.5 bg-emerald-500/5 border-2 border-transparent hover:border-emerald-500/20 rounded-xl hover:bg-card font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95"><Briefcase className="w-3.5 h-3.5 text-blue-600"/> LinkedIn</button>
                        <button onClick={() => addNode('task', 'TASK_INTERNAL')} className="p-2.5 bg-emerald-500/5 border-2 border-transparent hover:border-emerald-500/20 rounded-xl hover:bg-card font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> Action</button>
                        <div className="w-px h-6 bg-border/40 mx-1 self-center" />
                        <button onClick={() => addNode('stop', 'STOP')} className="p-2.5 border-2 border-transparent hover:border-rose-500/20 rounded-xl hover:bg-rose-500 hover:text-white font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95 text-rose-600"><Square className="w-3.5 h-3.5"/> End</button>
                        <div className="w-px h-6 bg-border/40 mx-1 self-center" />
                        <button onClick={() => setShowReminderBuilder(true)} className="p-2.5 bg-gradient-to-r from-violet-500/10 to-blue-500/10 border-2 border-violet-500/30 hover:border-violet-500/60 rounded-xl hover:shadow-lg font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95 text-violet-600"><Wand2 className="w-3.5 h-3.5"/> Quick Build</button>
                    </div>

                    {/* Right side: Actions */}
                    <div className="flex gap-3 pointer-events-auto">
                        <button 
                            onClick={() => setShowSimulator(!showSimulator)} 
                            className={cn(
                                "group h-11 px-6 rounded-2xl font-black text-[10px] uppercase tracking-[0.15em] shadow-xl transition-all flex items-center gap-3 border-2 active:scale-95",
                                showSimulator 
                                    ? "bg-card border-amber-500 text-amber-600 shadow-amber-500/10" 
                                    : "bg-card border-border/40 text-foreground hover:border-primary/40"
                            )}
                        >
                            <Play className={cn("w-4 h-4 transition-transform group-hover:scale-110", showSimulator ? "fill-amber-500" : "text-primary")} /> 
                            {showSimulator ? "Exit Simulator" : "Dry-Run Simulator"}
                        </button>
                        <div className={cn("flex gap-3 transition-all duration-500", showSimulator ? "opacity-30 pointer-events-none blur-sm" : "opacity-100")}>
                            <button onClick={onCancel} className="bg-card border-2 border-border/40 rounded-2xl px-6 h-11 font-black text-[10px] uppercase tracking-widest shadow-xl hover:bg-muted active:scale-95">Discard</button>
                            <button
                                onClick={handlePublish}
                                disabled={hasDraftNodes}
                                className={cn(
                                    "rounded-2xl px-6 h-11 font-black text-[10px] uppercase tracking-[0.2em] border-2 flex items-center gap-3 active:scale-95",
                                    hasDraftNodes
                                        ? "bg-muted text-muted-foreground border-border/60 cursor-not-allowed opacity-70"
                                        : "bg-primary text-primary-foreground shadow-[0_15px_30px_rgba(59,130,246,0.3)] border-primary/20 hover:bg-primary/90"
                                )}
                            >
                                <Save className="w-4 h-4"/> Publish Protocol
                            </button>
                        </div>
                    </div>
                </div>

                <div className={cn(
                    "w-full h-full transition-colors duration-700",
                    showSimulator ? "bg-amber-500/[0.03]" : "bg-transparent"
                )}>
                    <ReactFlow
                        nodes={nodes.map(n => ({ ...n, data: { ...n.data, stats: nodeStats[n.id] } }))}
                        edges={edges.map(e => ({
                            ...e,
                            label: edgeStats[e.id] ? `${edgeStats[e.id]} routes` : undefined,
                            labelStyle: { fill: '#64748b', fontWeight: 800, fontSize: 10, fontFamily: 'monospace' },
                            labelBgPadding: [6, 4],
                            labelBgBorderRadius: 4,
                            labelBgStyle: { fill: '#f8fafc', fillOpacity: 0.9, color: '#fff', stroke: '#cbd5e1', strokeWidth: 1 }
                        }))}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onSelectionChange={onSelectionChange}
                        nodeTypes={nodeTypes}
                        fitView
                        deleteKeyCode={['Backspace', 'Delete']}
                        connectionLineStyle={{ stroke: '#3b82f6', strokeWidth: 3 }}
                        defaultEdgeOptions={{ 
                            style: { strokeWidth: 3 },
                            markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' }
                        }}
                    >
                        <Background color={showSimulator ? "#f59e0b" : "#cbd5e1"} gap={25} size={1} opacity={showSimulator ? 0.2 : 0.4} />
                        <Controls className="!bg-card !border-2 !border-border/40 !rounded-xl !shadow-xl" />
                    </ReactFlow>
                </div>
            </div>

            <div className="w-[380px] bg-card border-l border-border/60 flex flex-col shadow-[-20px_0_80px_rgba(0,0,0,0.02)] relative z-20">
                <div className="p-8 border-b border-border/40 bg-accent/40 backdrop-blur-md sticky top-0 z-10">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-3 mb-1">
                        <Zap className="w-4 h-4 text-amber-500 fill-amber-500/20" /> 
                        {showSimulator ? 'Dry-Run Intelligence' : 'Node Auditor'}
                    </h3>
                    <div className="text-xl font-black text-foreground tracking-tight leading-none uppercase">
                        {showSimulator ? 'Simulator' : 'Properties'}
                    </div>
                </div>
                
                <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
                    {showSimulator ? (
                        <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-700">
                            <div className="space-y-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                    <h4 className="text-[11px] font-black uppercase tracking-widest text-foreground">Environmental Mock</h4>
                                </div>
                                
                                <div className="grid gap-3">
                                    <label className="flex items-center justify-between p-4 bg-muted border-2 border-transparent hover:border-border/60 rounded-[1.25rem] transition-all cursor-pointer group">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-[11px] font-bold text-foreground">Inbound Engagement</span>
                                            <span className="text-[9px] font-black text-muted-foreground uppercase tracking-tighter">Simulate Reply</span>
                                        </div>
                                        <input type="checkbox" checked={mockEvents.replied} onChange={e => setMockEvents({...mockEvents, replied: e.target.checked})} className="w-5 h-5 rounded-lg border-2 border-muted-foreground/30 text-primary transition-all focus:ring-0 bg-transparent" />
                                    </label>
 
                                    <label className="flex items-center justify-between p-4 bg-muted border-2 border-transparent hover:border-border/60 rounded-[1.25rem] transition-all cursor-pointer group">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-[11px] font-bold text-foreground">Delivery Failure</span>
                                            <span className="text-[9px] font-black text-muted-foreground uppercase tracking-tighter">Simulate Bounce</span>
                                        </div>
                                        <input type="checkbox" checked={mockEvents.bounced} onChange={e => setMockEvents({...mockEvents, bounced: e.target.checked})} className="w-5 h-5 rounded-lg border-2 border-muted-foreground/30 text-primary transition-all focus:ring-0 bg-transparent" />
                                    </label>
                                </div>
 
                                <div className="space-y-4 pt-4 border-t border-border/40">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Interaction Logic</span>
                                    <div className="grid gap-2">
                                        {[
                                            { id: 'complete', label: 'Human Resolution', desc: 'Operator completes tasks', state: mockEvents.completeTasks, setter: () => setMockEvents({...mockEvents, completeTasks: true, skipTasks: false}) },
                                            { id: 'skip', label: 'Manual Skip', desc: 'Operator bypasses node', state: mockEvents.skipTasks, setter: () => setMockEvents({...mockEvents, skipTasks: true, completeTasks: false}) },
                                            { id: 'halt', label: 'Indefinite Wait', desc: 'Execution stops at task', state: !mockEvents.completeTasks && !mockEvents.skipTasks, setter: () => setMockEvents({...mockEvents, completeTasks: false, skipTasks: false}) }
                                        ].map(opt => (
                                            <button 
                                                key={opt.id}
                                                onClick={opt.setter}
                                                className={cn(
                                                    "flex flex-col items-start p-4 rounded-[1.25rem] border-2 transition-all text-left",
                                                    opt.state ? "bg-primary/5 border-primary shadow-lg shadow-primary/5" : "bg-muted border-border/40 hover:border-border"
                                                )}
                                            >
                                                <span className={cn("text-[11px] font-bold leading-tight", opt.state ? "text-primary" : "text-foreground")}>{opt.label}</span>
                                                <span className="text-[9px] font-black uppercase tracking-tighter text-muted-foreground mt-1">{opt.desc}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                <button 
                                    onClick={handleRunSimulation} 
                                    disabled={isSimulating}
                                    className="w-full h-14 bg-foreground text-background font-black text-[11px] uppercase tracking-[0.25em] rounded-2xl shadow-2xl flex items-center justify-center gap-3 transition-all hover:opacity-90 active:scale-95 disabled:opacity-50"
                                >
                                    {isSimulating ? (
                                        <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <Play className="w-4 h-4 fill-current" />
                                    )}
                                    Run Scenario
                                </button>
                            </div>
                            
                            {simulationTrace && (
                                <div className="space-y-6 pt-8 border-t border-border/40 animate-in fade-in slide-in-from-bottom-4 duration-1000">
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-[11px] font-black uppercase tracking-widest text-foreground">Execution Trace</h4>
                                        <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-500 uppercase">{simulationTrace.length} Steps</span>
                                    </div>

                                    <div className="space-y-4 relative">
                                        <div className="absolute left-[15px] top-2 bottom-2 w-px bg-border/40" />
                                        {simulationTrace.map((event, idx) => (
                                            <div key={idx} className="relative pl-10 group">
                                                <div className="absolute left-0 top-1 w-8 h-8 rounded-full border-2 border-border bg-card shadow-md flex items-center justify-center z-10 transition-transform group-hover:scale-110">
                                                    <div className={cn(
                                                        "w-2 h-2 rounded-full",
                                                        event.actionType === 'ERROR' ? "bg-rose-500" : "bg-indigo-500"
                                                    )} />
                                                </div>
                                                <div className="bg-muted/50 p-4 rounded-2xl border border-transparent hover:border-border/60 transition-all">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="text-[10px] font-black text-foreground uppercase tracking-widest">{event.actionType}</span>
                                                        <span className="text-[10px] font-bold text-muted-foreground/60">Day {event.day}</span>
                                                    </div>
                                                    <div className="text-[10px] font-mono leading-relaxed text-muted-foreground whitespace-pre-wrap line-clamp-3 overflow-y-auto">
                                                        {JSON.stringify(event.details, null, 2)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                ) : !selectedNode ? (
                    <div className="h-full flex flex-col items-center justify-center text-center opacity-50 space-y-4">
                        <HelpCircle className="w-12 h-12" />
                        <p className="text-sm font-semibold max-w-[200px]">Select a node on the canvas to configure variables.</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        <div className="space-y-1">
                            <span className="text-[10px] text-muted-foreground font-bold uppercase">Node Id</span>
                            <div className="text-sm font-mono bg-background px-3 py-1.5 border border-border/60 rounded-md">{selectedNode.id}</div>
                        </div>

                        {(selectedNode.type === 'start' || selectedNode.type === 'stop') && (
                            <div className="space-y-1">
                                <span className="text-[10px] text-muted-foreground font-bold uppercase">Type</span>
                                <div className="text-sm font-bold">{selectedNode.type.toUpperCase()} BOUNDARY</div>
                                <p className="text-xs text-muted-foreground mt-2">Core sequence structural boundary. Flow cannot escape this without proper explicit routing connections downstream.</p>
                            </div>
                        )}

                        {selectedNode.type === 'email' && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between border-b pb-2">
                                    <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest">Email Configuration</span>
                                </div>

                                {selectedNode.data.isDraft && (
                                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex gap-3 animate-pulse">
                                        <Zap className="w-5 h-5 text-amber-500 shrink-0" />
                                        <div className="space-y-1">
                                            <p className="text-[10px] font-black text-amber-700 uppercase tracking-tight">Draft Template Active</p>
                                            <p className="text-[10px] leading-relaxed text-amber-600/80 font-medium">This node contains placeholder content from the Reminder Builder. Please edit and save to finalize.</p>
                                        </div>
                                    </div>
                                )}
                                
                                <div className="space-y-1.5 mt-4">
                                    <label className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest">Follow-up Stage</label>
                                    <select 
                                        className="w-full text-sm border border-border/60 bg-muted rounded-lg p-2.5 font-bold shadow-sm"
                                        value={selectedNode.data.stepLabel || 'INITIAL_EMAIL'}
                                        onChange={(e) => handleNodeDataChange('stepLabel', e.target.value)}
                                    >
                                        <option value="INITIAL_EMAIL">Initial Email</option>
                                        <option value="REMINDER_1">Reminder 1</option>
                                        <option value="REMINDER_2">Reminder 2</option>
                                        <option value="REMINDER_3">Reminder 3</option>
                                        <option value="FINAL_FOLLOW_UP">Final Follow-up</option>
                                        <option value="OTHER">Other / Custom</option>
                                    </select>
                                </div>
                                
                                <div className="bg-background border border-border/60 rounded-xl p-4 shadow-sm space-y-3 mt-4">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">Subject Preview</span>
                                        <span className="text-sm font-semibold truncate text-foreground">
                                            {selectedNode.data.variants?.length > 0 ? selectedNode.data.variants[0].subjectTemplate || 'No Subject' : (selectedNode.data.subjectTemplate || 'No Subject')}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 pt-2 border-t flex-wrap">
                                        <span className={`text-[9px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider ${selectedNode.data.sourceTemplateName || (selectedNode.data.variants?.length > 0 && selectedNode.data.variants[0].sourceTemplateName) ? 'bg-blue-500/10 text-blue-500 border border-blue-500/20' : 'bg-muted text-muted-foreground'}`}>
                                            {selectedNode.data.sourceTemplateName || (selectedNode.data.variants?.length > 0 && selectedNode.data.variants[0].sourceTemplateName) ? 'Template Copied' : 'Custom Copy'}
                                        </span>
                                        {selectedNode.data.variants?.length > 0 && (
                                            <span className="text-[9px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider bg-purple-500/10 text-purple-500 flex items-center gap-1 border border-purple-500/20">
                                                <Split className="w-3 h-3" /> A/B ({selectedNode.data.variants.length})
                                            </span>
                                        )}
                                        {((selectedNode.data.attachments?.length > 0) || (selectedNode.data.variants?.some(v => v.attachments?.length > 0))) && (
                                            <span className="text-[9px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-500 flex items-center gap-1 border border-emerald-500/20">
                                                <UploadCloud className="w-3 h-3" /> {selectedNode.data.attachments?.length || 0} Files
                                            </span>
                                        )}
                                        {selectedNode.data.senderAccountId ? (
                                            <span className="text-[9px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider bg-amber-500/10 text-amber-500 flex items-center gap-1 border border-amber-500/20">
                                                <UserCheck className="w-3 h-3" /> Specific Account
                                            </span>
                                        ) : selectedNode.data.senderPoolId ? (
                                            <span className="text-[9px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider bg-indigo-500/10 text-indigo-500 flex items-center gap-1 border border-indigo-500/20">
                                                <LayoutTemplate className="w-3 h-3" /> Pool Assignment
                                            </span>
                                        ) : (
                                            <span className="text-[9px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider bg-muted text-muted-foreground flex items-center gap-1 border border-border/60">
                                                <Zap className="w-3 h-3 font-normal" /> Default Routing
                                            </span>
                                        )}
                                    </div>
                                </div>
                                
                                <button 
                                    onClick={() => setEditingEmailNode(true)}
                                    className="w-full bg-primary text-primary-foreground font-bold py-2.5 rounded-lg shadow-sm hover:bg-primary/90 flex items-center justify-center gap-2 transition-all mt-4"
                                >
                                    <Mail className="w-4 h-4" /> Edit Email 
                                </button>
                                <div className="bg-blue-500/10 border border-blue-500/20 text-blue-700 text-[10px] p-3 rounded-lg font-medium leading-relaxed mt-2">
                                    Templating binds are executed identically against the assignment CRM records utilizing existing interpolation libraries prior to SMTP dispatch natively.
                                </div>
                            </div>
                        )}

                        {selectedNode.type === 'wait' && (
                            <div className="space-y-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] text-muted-foreground font-bold uppercase">Wait Strategy</label>
                                    <select 
                                        className="w-full text-sm border border-border/60 bg-muted rounded-lg p-2.5 font-bold shadow-sm"
                                        value={selectedNode.data.type || 'WAIT'}
                                        onChange={(e) => handleNodeDataChange('type', e.target.value)}
                                    >
                                        <option value="WAIT">Fixed Time Duration</option>
                                        <option value="WAIT_FOR_MEETING">Indefinite (Until Meeting Completes/No-Show)</option>
                                    </select>
                                </div>
                                
                                {(!selectedNode.data.type || selectedNode.data.type === 'WAIT') ? (
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] text-muted-foreground font-bold uppercase">Time to Wait</label>
                                        <div className="flex items-center gap-3">
                                            <input 
                                                type="number"
                                                min="1"
                                                className="w-24 text-sm border border-border/60 bg-muted rounded-lg p-2.5 font-bold shadow-sm"
                                                value={selectedNode.data.delayDays || 1}
                                                onChange={(e) => handleNodeDataChange('delayDays', parseInt(e.target.value))}
                                            />
                                            <span className="text-sm font-semibold text-muted-foreground">Days</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-3 bg-orange-500/10 border border-orange-500/20 text-orange-500 text-xs rounded-lg whitespace-pre-wrap leading-relaxed">
                                        <span className="font-bold">Execution Paused</span>{'\n'}
                                        Target halts securely resolving exclusively via Thread-Level Calendar Controls (No-Show, Completed, or Cancelled) triggering downstream organically.
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedNode.type === 'condition' && (
                            <div className="space-y-1.5">
                                <label className="text-[10px] text-muted-foreground font-bold uppercase">Evaluation Rule</label>
                                <select 
                                    className="w-full text-sm border border-border/60 bg-muted rounded-lg p-2.5 font-bold shadow-sm"
                                    value={selectedNode.data.type}
                                    onChange={(e) => handleNodeDataChange('type', e.target.value)}
                                >
                                    <optgroup label="Direct Communication">
                                        <option value="IF_REPLIED">If Contact Replied</option>
                                        <option value="IF_BOUNCED">If Email Bounced</option>
                                        <option value="IF_NO_REPLY">If No Reply Received</option>
                                    </optgroup>
                                    <optgroup label="Account / Colleague Context">
                                        <option value="IF_ACCOUNT_ACTIVE">If Org. Has Active Sequence Contact(s)</option>
                                        <option value="IF_ACCOUNT_REPLIED">If Org. Has Existing Inbound Engagements</option>
                                    </optgroup>
                                    <optgroup label="Calendar / Pipeline Status">
                                        <option value="IF_MEETING_BOOKED">If Prospect Meeting Booked</option>
                                        <option value="IF_MEETING_COMPLETED">If Meeting Completed Success</option>
                                        <option value="IF_MEETING_NO_SHOW">If Meeting Defaulted (No-Show)</option>
                                    </optgroup>
                                </select>
                                <div className="text-xs text-muted-foreground mt-3 p-3 bg-background border rounded-lg whitespace-pre-wrap leading-relaxed">
                                    <span className="font-bold">Edge Handles:</span>{'\n'}
                                    <span className="text-green-600 font-bold -ml-[2px]">• True</span>: Executed if condition evaluated truthy.{'\n'}
                                    <span className="text-red-500 font-bold -ml-[2px]">• False</span>: Default fallback escape path.
                                </div>
                            </div>
                        )}

                        {selectedNode.type === 'task' && (
                            <>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] text-muted-foreground font-bold uppercase">Task Type</label>
                                    <select 
                                        className="w-full text-sm border bg-background rounded-lg p-2.5 font-bold shadow-sm"
                                        value={selectedNode.data.type}
                                        onChange={(e) => handleNodeDataChange('type', e.target.value)}
                                    >
                                        <option value="TASK_INTERNAL">Internal Manual Task</option>
                                        <option value="TASK_LINKEDIN_MANUAL">LinkedIn Action</option>
                                        <option value="TASK_CALL">Phone Call</option>
                                    </select>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] text-muted-foreground font-bold uppercase">Action Title</label>
                                    <input 
                                        type="text"
                                        className="w-full text-sm border bg-background rounded-lg p-2.5 font-medium shadow-sm transition-all focus:border-primary"
                                        value={selectedNode.data.taskTitle || ''}
                                        onChange={(e) => handleNodeDataChange('taskTitle', e.target.value)}
                                        placeholder="e.g. Call decision maker..."
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] text-muted-foreground font-bold uppercase">Action Description</label>
                                    <textarea 
                                        className="w-full text-sm border border-border/60 bg-muted rounded-lg p-2.5 font-mono shadow-sm transition-all focus:border-primary"
                                        rows={4}
                                        value={selectedNode.data.taskDescription || ''}
                                        onChange={(e) => handleNodeDataChange('taskDescription', e.target.value)}
                                        placeholder="Specific instructions for operator..."
                                    />
                                </div>
                            </>
                        )}
                        
                        {selectedNode.type === 'update' && (
                            <>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] text-muted-foreground font-bold uppercase">Update Type</label>
                                    <select 
                                        className="w-full text-sm border border-border/60 bg-muted rounded-lg p-2.5 font-bold shadow-sm"
                                        value={selectedNode.data.type}
                                        onChange={(e) => handleNodeDataChange('type', e.target.value)}
                                    >
                                        <option value="STATUS_UPDATE">Set Contact Status</option>
                                        <option value="TAG_UPDATE">Add Contact Tag</option>
                                    </select>
                                </div>
                                {selectedNode.data.type === 'STATUS_UPDATE' && (
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] text-muted-foreground font-bold uppercase">New Status</label>
                                        <select 
                                            className="w-full text-sm border border-border/60 bg-muted rounded-lg p-2.5 font-bold shadow-sm"
                                            value={selectedNode.data.newStatus || 'INTERESTED'}
                                            onChange={(e) => handleNodeDataChange('newStatus', e.target.value)}
                                        >
                                            <option value="POTENTIAL">POTENTIAL</option>
                                            <option value="INTERESTED">INTERESTED</option>
                                            <option value="NOT_INTERESTED">NOT_INTERESTED</option>
                                            <option value="DO_NOT_CONTACT">DO_NOT_CONTACT</option>
                                            <option value="QUALIFIED">QUALIFIED</option>
                                        </select>
                                    </div>
                                )}
                                {selectedNode.data.type === 'TAG_UPDATE' && (
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] text-muted-foreground font-bold uppercase">Tag Name</label>
                                        <input 
                                            type="text"
                                            className="w-full text-sm border border-border/60 bg-muted rounded-lg p-2.5 font-bold shadow-sm"
                                            value={selectedNode.data.tagToAdd || ''}
                                            onChange={(e) => handleNodeDataChange('tagToAdd', e.target.value)}
                                            placeholder="e.g. VIP"
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        {hasDraftNodes && (
                            <div className="mt-8 p-5 bg-amber-500/5 border border-amber-500/20 rounded-2xl space-y-3">
                                <div className="flex items-center gap-2">
                                    <Zap className="w-4 h-4 text-amber-500" />
                                    <span className="text-[10px] font-black uppercase text-amber-700 tracking-widest">Global Draft Warning</span>
                                </div>
                                <p className="text-[11px] font-medium text-muted-foreground leading-relaxed">
                                    Your graph contains nodes with <span className="text-amber-600 font-bold underline">PLACEHOLDER CONTENT</span>. Please edit every "Draft" node before publishing to ensure your contacts receive high-quality emails.
                                </p>
                            </div>
                        )}

                        {selectedNode.type !== 'start' && (
                             <button onClick={handleDeleteNode} className="w-full mt-4 p-2.5 font-bold text-rose-500 text-sm border border-rose-500/20 bg-rose-500/5 rounded-xl hover:bg-rose-500/10 flex items-center justify-center gap-2 transition-colors">
                                <XCircle className="w-4 h-4" /> Delete Node
                             </button>
                        )}
                    </div>
                )}
            </div>
        </div>
            
            {editingEmailNode && selectedNode && selectedNode.type === 'email' && (
                <SequenceEmailEditorDrawer 
                    nodeData={selectedNode.data}
                    workspaceId={workspaceId}
                    onClose={() => setEditingEmailNode(false)}
                    onSave={(updatedData) => {
                        // Update local node state
                        let updatedNodes = nodes;
                        setNodes(nds => {
                            updatedNodes = nds.map(n => {
                                if (n.id === selectedNode.id) {
                                    const merged = { ...n, data: { ...n.data, ...updatedData, isDraft: false } };
                                    setSelectedNode(merged);
                                    return merged;
                                }
                                return n;
                            });
                            return updatedNodes;
                        });
                        setEditingEmailNode(false);
                        // Auto-persist graph to server so changes survive F5
                        // Use silent onAutoSave if available, fallback to onSave
                        const saveFn = onAutoSave || onSave;
                        setTimeout(() => {
                            saveFn(updatedNodes, edges);
                        }, 50);
                    }}
                />
            )}
        </div>
    </>
    );
}
